import type {
  DisplayOptions,
  FacetConfig,
  SearchFilters,
  SortField,
  SortOptions,
  SearchParams,
  SearchResult,
  SolrResponse,
  ResultHandler
} from '../../types';

import React from 'react';
import { deepEqual } from 'fast-equals';
import { debounce } from 'ts-debounce';

import {
  getFilterParams,
  getSortParams,
  getFacetParams,
  getSolrResult,
  mungeSearchResult
} from '../../utils';
import { appOptions } from '../../config';

import SearchResults from '../SearchResults/SearchResults';
import SearchFacets from '../SearchFacets/SearchFacets';
import SearchQuery from '../SearchQuery/SearchQuery';
import SearchInfo from '../NoSearch/NoSearch';
import NoResults from '../NoResults/NoResults';
import ShowIf from '../ShowIf';

import './styles.scss';

interface AppProps {
  resultHandler?: ResultHandler;
  options?: DisplayOptions;
  itemsPerPage?: number | null;
  showInitialResults?: boolean;
  solrURL: string;
  facets: FacetConfig[];
  sorts: SortField[];
  filters: SearchFilters;
  defaultSort: { field: string; direction: 'asc' | 'desc' };
  defaultFilter?: SearchFilters;
}

export interface AppState {
  options: DisplayOptions;
  response: SolrResponse<SearchResult> | null;
  query: string;
  pending: boolean;
  filters: SearchFilters;
  facets: FacetConfig[];
  sort: SortOptions;
  updated: number | null;
  error: string | null;
  page: number;
}

export default class App extends React.Component<AppProps, AppState> {
  public state: AppState = {
    options: appOptions.display,
    response: null,
    query: '',
    pending: false,
    facets: [],
    filters: {},
    sort: { field: 'score', direction: 'desc' },
    error: null,
    updated: null,
    page: 1
  };

  protected initial: {
    sort: SortOptions;
    query: string;
    filters: SearchFilters;
  } = { query: '', sort: { field: 'score', direction: 'desc' }, filters: {} };

  /**
   * I'm not really a fan of this approach, but I've yet to find a better one—
   * only re-run if the update was from a query change.
   */
  public componentDidUpdate(prevProps: AppProps, prevState: AppState): void {
    if (this.state.updated && this.state.updated !== prevState.updated) {
      // if they've reverted to the original search state, display the "enter keyword" text
      const sortQueryChange =
        this.state.query.trim() !== this.initial.query.trim() ||
        !deepEqual(this.state.sort, this.initial.sort);

      // no guarantee that all the keys in state will be in initial, and vice-versa
      const keys = new Set([
        ...Object.keys(this.initial.filters),
        ...Object.keys(this.state.filters)
      ]);

      const filtersChanged = Array.from(keys).filter(
        key => this.initial.filters[key] !== this.state.filters[key]
      );

      const onlyLegacyChange =
        filtersChanged.length === 1 &&
        this.state.filters['bs_field_legacy_document'] !==
          this.initial.filters['bs_field_legacy_document'];

      const noSearch =
        // no changes have been made to query or sorts
        !sortQueryChange &&
        // either no filters have been changed (since mount), or only the legacy change filter has been changed
        (filtersChanged.length === 0 || onlyLegacyChange);

      if (noSearch) {
        if (this.state.response) {
          // "enter keywords"
          this.setState({ response: null });
        }
      } else {
        this.handle.onSearch();
      }
    }
  }

  public componentDidMount(): void {
    const params = new URLSearchParams(window.location.search);
    const sortDir = params.get('sort_direction') ?? 'desc';
    const sortField = params.get('sort_field') ?? 'score';
    const sort = {
      ...this.props.defaultSort,
      field: sortField,
      direction: sortDir as 'asc' | 'desc' | null
    };

    const filters = {
      ...this.props.filters,
      ...(this.props.defaultFilter ?? {})
    };

    this.initial = {
      sort,
      query: this.state.query,
      filters
    };

    this.setState({
      options: this.props.options ?? this.state.options,
      updated: this.props.showInitialResults ?? true ? Date.now() : null,
      facets: this.props.facets,
      filters,
      sort
    });
  }

  public handle = {
    onSearch: debounce(() => {
      this.setState({ pending: true }, async () => {
        try {
          const res = await getSolrResult(this.props.solrURL, this.params);
          const page = Math.floor(
            (res?.response?.start ?? 1) / (this.props.itemsPerPage ?? 1)
          );
          this.setState(
            {
              pending: false,
              error: null,
              page: page + 1 ?? 1,
              response: {
                ...res,
                response: {
                  ...res?.response,
                  docs: res?.response.docs?.map(mungeSearchResult)
                }
              }
            },
            () => {
              // scroll to top on query or pager changes
              window.scrollTo(0, 0);
            }
          );
        } catch (e) {
          this.setState({ pending: false, error: e.message, response: null });
        }
      });
    }, 250),
    onChangeSort: (sort: SortOptions | null) => {
      this.setState({
        sort: sort ?? { field: null, direction: null },
        updated: Date.now()
      });
    },
    onChangeQuery: (value: string | null) => {
      this.setState({ updated: Date.now(), query: value ?? '' });
    },
    onChangePage: (page: number) => {
      this.setState({ page, updated: Date.now() });
    },
    onChangeFacet: (
      key: string,
      value?: OfOrArrayOf<string | number | boolean> | null
    ): void => {
      const onChange = this.props.facets.find(i => i.name === key)?.onChange;
      const nextValue = value && onChange ? onChange(value) : value ?? null;
      const filters = { ...this.state.filters, [key]: nextValue };
      if (nextValue === null) {
        delete filters[key];
      }
      this.setState({ updated: Date.now(), filters });
    }
  };

  public get params(): Partial<SearchParams> {
    const { sort, query, filters, facets } = this.state;
    const limit = this.props.itemsPerPage ?? 25;
    const offset = (this.state.page - 1) * limit;

    const res = {
      query: query ?? (this.props.showInitialResults ? '*:*' : query),
      solrSearchUrl: this.props.solrURL,
      sort: getSortParams({ sort }),
      facet: getFacetParams({ facets, query }),
      filter: getFilterParams({ filters, facets, query }),
      offset,
      limit
    };

    return Object.entries(res).reduce((a, [key, value]) => {
      return value ? { ...a, [key]: value } : a;
    }, {}) as Partial<SearchParams>;
  }

  public render(): JSX.Element {
    const Results = this.props.resultHandler ?? SearchResults;
    const currentPath = window.location.pathname;
    let docs = this.state.response?.response.docs ?? null;
    if (!docs?.length) {
      docs = null;
    }
    const noResponse = !this.state.response;

    return (
      <>
        <div className="search-container">
          <div className="search-filters">
            <ShowIf value={this.state.options.searchQuery}>
              <div className="search-query">
                <SearchQuery
                  query={this.state.query}
                  onChange={this.handle.onChangeQuery}
                />
              </div>
            </ShowIf>
            <ShowIf value={this.state.options.searchFacets}>
              <SearchFacets
                response={this.state.response}
                facets={this.props.facets ?? []}
                filters={this.state.filters}
                onChange={this.handle.onChangeFacet}
              />
            </ShowIf>
            <div className="reset">
              <a href={currentPath} className="btn btn-light">
                Reset
              </a>
            </div>
          </div>
          <ShowIf value={docs}>
            {results => (
              <Results
                options={this.state.options}
                results={results}
                pagination={{
                  page: this.state.page,
                  perPage: this.props.itemsPerPage ?? 25,
                  total: this.state.response?.response.numFound ?? 0,
                  onChange: this.handle.onChangePage
                }}
                sort={{
                  field: this.state.sort.field,
                  direction: this.state.sort.direction,
                  onChange: this.handle.onChangeSort
                }}
              />
            )}
          </ShowIf>
          <ShowIf value={noResponse}>
            <SearchInfo />
          </ShowIf>
          <ShowIf value={!noResponse && !docs}>
            <NoResults />
          </ShowIf>
        </div>
      </>
    );
  }
}
